/**
 * Created by Ning Jiang on 11/23/2016.
 */

import {Back, Ease, Power3} from "gsap";

import {ILayersConfig} from "@nolimitcity/slot-game/bin/core/stage/ILayersConfig";
import {LayerScaleOption} from "@nolimitcity/slot-game/bin/core/stage/LayerScaleOption";
import {IGameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/IGameConfig";
import {IDeviceDependentConfig} from "@nolimitcity/slot-game/bin/core/useragent/UserAgent";
import {IWinRatios, WinRatioType} from "@nolimitcity/slot-game/bin/core/winpresentation/WinRatio";
import {BetWinMode} from "@nolimitcity/slot-game/bin/core/gametype/BetWinMode";
import {SpinMode} from "@nolimitcity/slot-game/bin/core/gametype/SpinMode";
import {IFITHSymbolStates} from "./symbols/FITHReelSymbol";
import {FITHGameAssets} from "./FITHGameAssets";
import {IReelSets} from "@nolimitcity/slot-game/bin/core/reel/reelset/IReelSets";
import {FITHLayerConfig} from "./stage/FITHLayerConfig";
import {ISymbolStateConfig} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";

declare module "@nolimitcity/slot-game/bin/core/winpresentation/WinRatio" {
    export enum WinRatioType {
        SUPER_SUPER_MEGA_WIN = "superSuperMegaWin",
        SUPER_DUPER_MEGA_WIN = "superDuperMegaWin"
    }
}

const temp = WinRatioType as any;
temp["SUPER_SUPER_MEGA_WIN"] = "superSuperMegaWin";
temp["SUPER_DUPER_MEGA_WIN"] = "superDuperMegaWin";

export class FITHGameConfig implements IGameConfig {
    public static instance: FITHGameConfig = new FITHGameConfig();

    BROWSER_FPS: number = 30;

    GAME_WIDTH: number = 1280;
    GAME_HEIGHT: number = 720;

    DEFAULT_LAYER_SCALE_OPTION: IDeviceDependentConfig<LayerScaleOption> = FITHLayerConfig.defaultLayerScaleOptions;

    // An controller for the Z-layers for created PIXI.containers. Managed by StageManager.
    LAYERS: ILayersConfig = FITHLayerConfig.getConfig();

    BET_WIN_MODE: BetWinMode = BetWinMode.BET_WAY;
    SPIN_MODE: SpinMode = SpinMode.AVALANCHE;

    REEL_AREA_POS_X: number = 0;
    REEL_AREA_POS_Y: number = 148;
    REEL_AREA_POS_X_BONUS_GAME: number = 18;
    REEL_AREA_POS_Y_BONUS_GAME: number = 160;
    REEL_AREA_WIDTH: number = 720;
    REEL_AREA_HEIGHT: number = 540;
    REEL_AREA_HEIGHT_BONUS_GAME: number = 516;
    REEL_AREA_MASK: boolean = true;
    REEL_AREA_MASK_RECT: PIXI.Rectangle = new PIXI.Rectangle(0, this.REEL_AREA_POS_Y - 20, this.REEL_AREA_WIDTH, this.REEL_AREA_HEIGHT + 20);

    SYMBOL_STATES: IFITHSymbolStates = {
        normal: {
            prio: 0,
            keyword: "normal",
            texturesCreator: (symName: string) => {
                return FITHGameAssets.getSymbolStateTextures(symName);
            }
        },
        spin: {
            prio: 1,
            keyword: "spin",
            texturesCreator: (symName: string, stateKey: string) => {
                return FITHGameAssets.getSymbolStateTextures(symName, stateKey);
            }
        },
        land: {
            prio: 1,
            keyword: "land",
            texturesCreator: (symName: string) => {
                return FITHGameAssets.getSymbolStateTextures(symName);
            }
        },
        multi: {
            prio: 1,
            keyword: "multi",
            texturesCreator: (symName: string) => {
                return FITHGameAssets.getSymbolStateTextures(symName);
            }
        },
        noWin: {
            prio: 2,
            keyword: "noWin",
            texturesCreator: (symName: string) => {
                return FITHGameAssets.getSymbolStateTextures(symName);
            }
        },
        win: {
            prio: 3,
            keyword: "win",
            texturesCreator: (symName: string) => {
                return FITHGameAssets.getSymbolStateTextures(symName);
            }
        },
        hidden: {
            prio: 4,
            keyword: "hidden"
        },
    };
    SYMBOL_WIDTH: number = 120;
    SYMBOL_HEIGHT: number = 90;
    SYMBOL_WIDTH_BONUS_GAME: number = 114;
    SYMBOL_HEIGHT_BONUS_GAME: number = 86;
    SYMBOL_BOTTOM_PADDING: number = 0;

    REELS_NUM_ORIGINAL: number = 6; // this is the original reels we have in the game.
    REELS_NUM: number = 36; // this is (6 original reels * SYMBOLS_NUM_IN_REEL[2]), so that we can have spin on each symbol in bonus game.
    SPACE_BETWEEN_REELS: number = 0;
    REEL_DISPLAY_POSITIONS: [number, number][] = [
        [this.SYMBOL_WIDTH * 0.5, 0],
        [this.SYMBOL_WIDTH * 1.5 + this.SPACE_BETWEEN_REELS, 0],
        [this.SYMBOL_WIDTH * 2.5 + this.SPACE_BETWEEN_REELS * 2, 0],
        [this.SYMBOL_WIDTH * 3.5 + this.SPACE_BETWEEN_REELS * 3, 0],
        [this.SYMBOL_WIDTH * 4.5 + this.SPACE_BETWEEN_REELS * 4, 0],
        [this.SYMBOL_WIDTH * 5.5 + this.SPACE_BETWEEN_REELS * 5, 0],
        [this.SYMBOL_WIDTH * 0.5, 0],
        [this.SYMBOL_WIDTH * 1.5 + this.SPACE_BETWEEN_REELS, 0],
        [this.SYMBOL_WIDTH * 2.5 + this.SPACE_BETWEEN_REELS * 2, 0],
        [this.SYMBOL_WIDTH * 3.5 + this.SPACE_BETWEEN_REELS * 3, 0],
        [this.SYMBOL_WIDTH * 4.5 + this.SPACE_BETWEEN_REELS * 4, 0],
        [this.SYMBOL_WIDTH * 5.5 + this.SPACE_BETWEEN_REELS * 5, 0],
        [this.SYMBOL_WIDTH * 0.5, 0],
        [this.SYMBOL_WIDTH * 1.5 + this.SPACE_BETWEEN_REELS, 0],
        [this.SYMBOL_WIDTH * 2.5 + this.SPACE_BETWEEN_REELS * 2, 0],
        [this.SYMBOL_WIDTH * 3.5 + this.SPACE_BETWEEN_REELS * 3, 0],
        [this.SYMBOL_WIDTH * 4.5 + this.SPACE_BETWEEN_REELS * 4, 0],
        [this.SYMBOL_WIDTH * 5.5 + this.SPACE_BETWEEN_REELS * 5, 0],
        [this.SYMBOL_WIDTH * 0.5, 0],
        [this.SYMBOL_WIDTH * 1.5 + this.SPACE_BETWEEN_REELS, 0],
        [this.SYMBOL_WIDTH * 2.5 + this.SPACE_BETWEEN_REELS * 2, 0],
        [this.SYMBOL_WIDTH * 3.5 + this.SPACE_BETWEEN_REELS * 3, 0],
        [this.SYMBOL_WIDTH * 4.5 + this.SPACE_BETWEEN_REELS * 4, 0],
        [this.SYMBOL_WIDTH * 5.5 + this.SPACE_BETWEEN_REELS * 5, 0],
        [this.SYMBOL_WIDTH * 0.5, 0],
        [this.SYMBOL_WIDTH * 1.5 + this.SPACE_BETWEEN_REELS, 0],
        [this.SYMBOL_WIDTH * 2.5 + this.SPACE_BETWEEN_REELS * 2, 0],
        [this.SYMBOL_WIDTH * 3.5 + this.SPACE_BETWEEN_REELS * 3, 0],
        [this.SYMBOL_WIDTH * 4.5 + this.SPACE_BETWEEN_REELS * 4, 0],
        [this.SYMBOL_WIDTH * 5.5 + this.SPACE_BETWEEN_REELS * 5, 0],
        [this.SYMBOL_WIDTH * 0.5, 0],
        [this.SYMBOL_WIDTH * 1.5 + this.SPACE_BETWEEN_REELS, 0],
        [this.SYMBOL_WIDTH * 2.5 + this.SPACE_BETWEEN_REELS * 2, 0],
        [this.SYMBOL_WIDTH * 3.5 + this.SPACE_BETWEEN_REELS * 3, 0],
        [this.SYMBOL_WIDTH * 4.5 + this.SPACE_BETWEEN_REELS * 4, 0],
        [this.SYMBOL_WIDTH * 5.5 + this.SPACE_BETWEEN_REELS * 5, 0]
    ];
    REEL_DISPLAY_POSITIONS_BONUS_GAME: [number, number][] = [
        [this.SYMBOL_WIDTH_BONUS_GAME * 0.5 + this.REEL_AREA_POS_X_BONUS_GAME, 0], // 0
        [this.SYMBOL_WIDTH_BONUS_GAME * 0.5 + this.REEL_AREA_POS_X_BONUS_GAME, 0], // 6
        [this.SYMBOL_WIDTH_BONUS_GAME * 0.5 + this.REEL_AREA_POS_X_BONUS_GAME, 0], // 12
        [this.SYMBOL_WIDTH_BONUS_GAME * 0.5 + this.REEL_AREA_POS_X_BONUS_GAME, 0], // 18
        [this.SYMBOL_WIDTH_BONUS_GAME * 0.5 + this.REEL_AREA_POS_X_BONUS_GAME, 0], // 24
        [this.SYMBOL_WIDTH_BONUS_GAME * 0.5 + this.REEL_AREA_POS_X_BONUS_GAME, 0], // 30
        [this.SYMBOL_WIDTH_BONUS_GAME * 1.5 + this.REEL_AREA_POS_X_BONUS_GAME + this.SPACE_BETWEEN_REELS, 0], // 1
        [this.SYMBOL_WIDTH_BONUS_GAME * 1.5 + this.REEL_AREA_POS_X_BONUS_GAME + this.SPACE_BETWEEN_REELS, 0], // 7
        [this.SYMBOL_WIDTH_BONUS_GAME * 1.5 + this.REEL_AREA_POS_X_BONUS_GAME + this.SPACE_BETWEEN_REELS, 0], // 13
        [this.SYMBOL_WIDTH_BONUS_GAME * 1.5 + this.REEL_AREA_POS_X_BONUS_GAME + this.SPACE_BETWEEN_REELS, 0], // 19
        [this.SYMBOL_WIDTH_BONUS_GAME * 1.5 + this.REEL_AREA_POS_X_BONUS_GAME + this.SPACE_BETWEEN_REELS, 0], // 25
        [this.SYMBOL_WIDTH_BONUS_GAME * 1.5 + this.REEL_AREA_POS_X_BONUS_GAME + this.SPACE_BETWEEN_REELS, 0], // 31      and so on for the below
        [this.SYMBOL_WIDTH_BONUS_GAME * 2.5 + this.REEL_AREA_POS_X_BONUS_GAME + this.SPACE_BETWEEN_REELS * 2, 0],
        [this.SYMBOL_WIDTH_BONUS_GAME * 2.5 + this.REEL_AREA_POS_X_BONUS_GAME + this.SPACE_BETWEEN_REELS * 2, 0],
        [this.SYMBOL_WIDTH_BONUS_GAME * 2.5 + this.REEL_AREA_POS_X_BONUS_GAME + this.SPACE_BETWEEN_REELS * 2, 0],
        [this.SYMBOL_WIDTH_BONUS_GAME * 2.5 + this.REEL_AREA_POS_X_BONUS_GAME + this.SPACE_BETWEEN_REELS * 2, 0],
        [this.SYMBOL_WIDTH_BONUS_GAME * 2.5 + this.REEL_AREA_POS_X_BONUS_GAME + this.SPACE_BETWEEN_REELS * 2, 0],
        [this.SYMBOL_WIDTH_BONUS_GAME * 2.5 + this.REEL_AREA_POS_X_BONUS_GAME + this.SPACE_BETWEEN_REELS * 2, 0],
        [this.SYMBOL_WIDTH_BONUS_GAME * 3.5 + this.REEL_AREA_POS_X_BONUS_GAME + this.SPACE_BETWEEN_REELS * 3, 0],
        [this.SYMBOL_WIDTH_BONUS_GAME * 3.5 + this.REEL_AREA_POS_X_BONUS_GAME + this.SPACE_BETWEEN_REELS * 3, 0],
        [this.SYMBOL_WIDTH_BONUS_GAME * 3.5 + this.REEL_AREA_POS_X_BONUS_GAME + this.SPACE_BETWEEN_REELS * 3, 0],
        [this.SYMBOL_WIDTH_BONUS_GAME * 3.5 + this.REEL_AREA_POS_X_BONUS_GAME + this.SPACE_BETWEEN_REELS * 3, 0],
        [this.SYMBOL_WIDTH_BONUS_GAME * 3.5 + this.REEL_AREA_POS_X_BONUS_GAME + this.SPACE_BETWEEN_REELS * 3, 0],
        [this.SYMBOL_WIDTH_BONUS_GAME * 3.5 + this.REEL_AREA_POS_X_BONUS_GAME + this.SPACE_BETWEEN_REELS * 3, 0],
        [this.SYMBOL_WIDTH_BONUS_GAME * 4.5 + this.REEL_AREA_POS_X_BONUS_GAME + this.SPACE_BETWEEN_REELS * 4, 0],
        [this.SYMBOL_WIDTH_BONUS_GAME * 4.5 + this.REEL_AREA_POS_X_BONUS_GAME + this.SPACE_BETWEEN_REELS * 4, 0],
        [this.SYMBOL_WIDTH_BONUS_GAME * 4.5 + this.REEL_AREA_POS_X_BONUS_GAME + this.SPACE_BETWEEN_REELS * 4, 0],
        [this.SYMBOL_WIDTH_BONUS_GAME * 4.5 + this.REEL_AREA_POS_X_BONUS_GAME + this.SPACE_BETWEEN_REELS * 4, 0],
        [this.SYMBOL_WIDTH_BONUS_GAME * 4.5 + this.REEL_AREA_POS_X_BONUS_GAME + this.SPACE_BETWEEN_REELS * 4, 0],
        [this.SYMBOL_WIDTH_BONUS_GAME * 4.5 + this.REEL_AREA_POS_X_BONUS_GAME + this.SPACE_BETWEEN_REELS * 4, 0],
        [this.SYMBOL_WIDTH_BONUS_GAME * 5.5 + this.REEL_AREA_POS_X_BONUS_GAME + this.SPACE_BETWEEN_REELS * 5, 0],
        [this.SYMBOL_WIDTH_BONUS_GAME * 5.5 + this.REEL_AREA_POS_X_BONUS_GAME + this.SPACE_BETWEEN_REELS * 5, 0],
        [this.SYMBOL_WIDTH_BONUS_GAME * 5.5 + this.REEL_AREA_POS_X_BONUS_GAME + this.SPACE_BETWEEN_REELS * 5, 0],
        [this.SYMBOL_WIDTH_BONUS_GAME * 5.5 + this.REEL_AREA_POS_X_BONUS_GAME + this.SPACE_BETWEEN_REELS * 5, 0],
        [this.SYMBOL_WIDTH_BONUS_GAME * 5.5 + this.REEL_AREA_POS_X_BONUS_GAME + this.SPACE_BETWEEN_REELS * 5, 0],
        [this.SYMBOL_WIDTH_BONUS_GAME * 5.5 + this.REEL_AREA_POS_X_BONUS_GAME + this.SPACE_BETWEEN_REELS * 5, 0]
    ];
    SYMBOLS_NUM_IN_REEL: [number, number, number, number, number] = [0, 0, 6, 0, 0];

    REEL_SPIN_TIME: number = 1.3;
    REEL_SPIN_TIME_AVALANCHE: number = 0.1;
    REEL_SPIN_TIME_BONUS_GAME: number = 0.1;
    REEL_FAST_SPIN_SPIN_TIME: number = 0.8;
    MIN_STOPPING_TIME: number = 0.4; // this effects all the JU which has min. spin time defined for them.

    REEL_SPIN_START_SYMBOL_CHANGE_TO_SPIN_STATE_DURATION: number = 0.3;
    REEL_SPIN_START_SYMBOL_CHANGE_TO_SPIN_STATE_DELAY: number = 0.3;
    REEL_SPIN_START_EASE: Ease = Power3.easeIn;
    REEL_SPIN_START_EASE_BONUS_GAME: Ease = Back.easeIn.config(3);
    REEL_SPIN_STOP_SYMBOL_CHANGE_FROM_SPIN_STATE_DURATION: number = 0.2;
    REEL_SPIN_STOP_SYMBOL_CHANGE_FROM_SPIN_STATE_DELAY: number = 0;
    REEL_SPIN_STOP_ELASTIC_CONFIG: [number, number] = [1, 1];
    REEL_SPIN_QUICK_STOP_ELASTIC_CONFIG: [number, number] = [1, 1];
    REEL_SPIN_STOP_ELASTIC_CONFIG_BONUS_GAME: [number, number] = [1.5, 0.95];
    REEL_SPIN_QUICK_STOP_ELASTIC_CONFIG_BONUS_GAME: [number, number] = [1.2, 0.75];

    REEL_SPIN_START_SPEED: number = 0.5;
    REEL_SPIN_START_SPEED_BONUS_GAME: number = 0.2;
    REEL_SPIN_SPIN_SPEED: number = 0.07;
    REEL_NEAR_WIN_SPIN_SPEED: number = 0.25; // #226
    REEL_SPIN_STOP_SPEED: number = 0.25;
    REEL_SPIN_QUICK_STOP_SPEED: number = 0.23;

    REEL_STOP_DELAY: number = 0;
    REEL_STOP_DELAY_BONUS_GAME: number[] = []; // this is set in FITHBonusGameSpinView.ts class.
    REEL_QUICK_STOP_DELAY: number = 0;
    REEL_NEAR_WIN_STOP_DELAY: number = 0;

    REEL_SPIN_START_SYMBOL_DELAY: number = 0.05;
    REEL_FAST_SPIN_START_SYMBOL_DELAY: number = 0.05;
    REEL_SPIN_STOP_SYMBOL_DELAY: number = 0.1;
    REEL_SPIN_QUICK_STOP_SYMBOL_DELAY: number = 0.08;
    REEL_SPIN_NEAR_WIN_STOP_SYMBOL_DELAY: number = 0;

    NEAR_WIN_NON_D_REEL_COUNT: number = 0;
    NEAR_WIN_LAST_SCATTER_REEL_ID: number = (this.REELS_NUM + 1);
    NEAR_WIN_DELAY: number = 0.7; // #226

    STAGE_INIT_Y: number = 0; // setting it in FITHBigWinView.ts

    REEL_SETS: IReelSets = {
        "BASE_REELSET": [
            ["M1", "L1", "L3", "M2", "L2", "M4", "L5", "M3", "W", "L4", "M5"],
            ["M1", "L1", "L3", "M2", "L2", "M4", "L5", "M3", "W", "L4", "M5"],
            ["M1", "L1", "L3", "M2", "L2", "M4", "L5", "M3", "W", "L4", "M5"],
            ["M1", "L1", "L3", "M2", "L2", "M4", "L5", "M3", "W", "L4", "M5"],
            ["M1", "L1", "L3", "M2", "L2", "M4", "L5", "M3", "W", "L4", "M5"],
            ["M1", "L1", "L3", "M2", "L2", "M4", "L5", "M3", "W", "L4", "M5"],

            ["M1", "L1", "L3", "M2", "L2", "M4", "L5", "M3", "W", "L4", "M5"],
            ["M1", "L1", "L3", "M2", "L2", "M4", "L5", "M3", "W", "L4", "M5"],
            ["M1", "L1", "L3", "M2", "L2", "M4", "L5", "M3", "W", "L4", "M5"],
            ["M1", "L1", "L3", "M2", "L2", "M4", "L5", "M3", "W", "L4", "M5"],
            ["M1", "L1", "L3", "M2", "L2", "M4", "L5", "M3", "W", "L4", "M5"],
            ["M1", "L1", "L3", "M2", "L2", "M4", "L5", "M3", "W", "L4", "M5"],

            ["M1", "L1", "L3", "M2", "L2", "M4", "L5", "M3", "W", "L4", "M5"],
            ["M1", "L1", "L3", "M2", "L2", "M4", "L5", "M3", "W", "L4", "M5"],
            ["M1", "L1", "L3", "M2", "L2", "M4", "L5", "M3", "W", "L4", "M5"],
            ["M1", "L1", "L3", "M2", "L2", "M4", "L5", "M3", "W", "L4", "M5"],
            ["M1", "L1", "L3", "M2", "L2", "M4", "L5", "M3", "W", "L4", "M5"],
            ["M1", "L1", "L3", "M2", "L2", "M4", "L5", "M3", "W", "L4", "M5"],

            ["M1", "L1", "L3", "M2", "L2", "M4", "L5", "M3", "W", "L4", "M5"],
            ["M1", "L1", "L3", "M2", "L2", "M4", "L5", "M3", "W", "L4", "M5"],
            ["M1", "L1", "L3", "M2", "L2", "M4", "L5", "M3", "W", "L4", "M5"],
            ["M1", "L1", "L3", "M2", "L2", "M4", "L5", "M3", "W", "L4", "M5"],
            ["M1", "L1", "L3", "M2", "L2", "M4", "L5", "M3", "W", "L4", "M5"],
            ["M1", "L1", "L3", "M2", "L2", "M4", "L5", "M3", "W", "L4", "M5"],

            ["M1", "L1", "L3", "M2", "L2", "M4", "L5", "M3", "W", "L4", "M5"],
            ["M1", "L1", "L3", "M2", "L2", "M4", "L5", "M3", "W", "L4", "M5"],
            ["M1", "L1", "L3", "M2", "L2", "M4", "L5", "M3", "W", "L4", "M5"],
            ["M1", "L1", "L3", "M2", "L2", "M4", "L5", "M3", "W", "L4", "M5"],
            ["M1", "L1", "L3", "M2", "L2", "M4", "L5", "M3", "W", "L4", "M5"],
            ["M1", "L1", "L3", "M2", "L2", "M4", "L5", "M3", "W", "L4", "M5"],

            ["M1", "L1", "L3", "M2", "L2", "M4", "L5", "M3", "W", "L4", "M5"],
            ["M1", "L1", "L3", "M2", "L2", "M4", "L5", "M3", "W", "L4", "M5"],
            ["M1", "L1", "L3", "M2", "L2", "M4", "L5", "M3", "W", "L4", "M5"],
            ["M1", "L1", "L3", "M2", "L2", "M4", "L5", "M3", "W", "L4", "M5"],
            ["M1", "L1", "L3", "M2", "L2", "M4", "L5", "M3", "W", "L4", "M5"],
            ["M1", "L1", "L3", "M2", "L2", "M4", "L5", "M3", "W", "L4", "M5"]
        ],
        "FREESPIN_REELSET": [
            ["X", "X"],
            ["V", "V", "V", "V", "X", "V", "V", "X", "V"],
            ["V", "X", "V", "V", "X", "V", "V"],
            ["V", "V", "X", "V", "V", "X", "V"],
            ["V", "V", "V", "X", "V", "V", "V", "X"],
            ["V", "V", "V", "X", "V", "V", "V", "V", "X"],

            ["X", "X"],
            ["V", "V", "V", "V", "X", "V", "V", "X", "V"],
            ["V", "X", "V", "V", "X", "V", "V"],
            ["V", "V", "X", "V", "V", "X", "V"],
            ["V", "V", "V", "X", "V", "V", "V", "X"],
            ["V", "V", "V", "X", "V", "V", "V", "V", "X"],

            ["X", "X"],
            ["V", "V", "V", "V", "X", "V", "V", "X", "V"],
            ["V", "X", "V", "V", "X", "V", "V"],
            ["V", "V", "X", "V", "V", "X", "V"],
            ["V", "V", "V", "X", "V", "V", "V", "X"],
            ["V", "V", "V", "X", "V", "V", "V", "V", "X"],

            ["X", "X"],
            ["V", "V", "V", "V", "X", "V", "V", "X", "V"],
            ["V", "X", "V", "V", "X", "V", "V"],
            ["V", "V", "X", "V", "V", "X", "V"],
            ["V", "V", "V", "X", "V", "V", "V", "X"],
            ["V", "V", "V", "X", "V", "V", "V", "V", "X"],

            ["X", "X"],
            ["V", "V", "V", "V", "X", "V", "V", "X", "V"],
            ["V", "X", "V", "V", "X", "V", "V"],
            ["V", "V", "X", "V", "V", "X", "V"],
            ["V", "V", "V", "X", "V", "V", "V", "X"],
            ["V", "V", "V", "X", "V", "V", "V", "V", "X"],

            ["X", "X"],
            ["V", "V", "V", "V", "X", "V", "V", "X", "V"],
            ["V", "X", "V", "V", "X", "V", "V"],
            ["V", "V", "X", "V", "V", "X", "V"],
            ["V", "V", "V", "X", "V", "V", "V", "X"],
            ["V", "V", "V", "X", "V", "V", "V", "V", "X"]
        ]
    };

    WIN_RATIOS: IWinRatios = {
        normalWin: [
            {
                ratio: 0,
                text: "Tiny Win",
                duration: 0,
                type: WinRatioType.TINY
            },
            {
                ratio: 1.5,
                text: "Small Win",
                duration: 0,
                type: WinRatioType.SMALL
            },
            {
                ratio: 3,
                text: "Medium Win",
                duration: 1.3,
                type: WinRatioType.MEDIUM
            },
            {
                ratio: 5,
                text: "Large Win",
                duration: 1.6,
                type: WinRatioType.LARGE
            }
        ],
        bigWin: [
            {
                ratio: 15,
                text: "Playing with Fire",
                duration: 4.036,
                type: WinRatioType.BIG_WIN
            },
            {
                ratio: 25,
                text: "Hmm....A Bomb",
                duration: 3.898,
                type: WinRatioType.MEGA_WIN

            },
            {
                ratio: 50,
                text: "Fire in the Hole",
                duration: 3.898,
                type: WinRatioType.SUPER_MEGA_WIN
            },
            {
                ratio: 100,
                text: "KABOOOOOM",
                duration: 3.898,
                type: WinRatioType.SUPER_SUPER_MEGA_WIN
            },
            {
                ratio: 1000,
                text: "I want MORE!!",
                duration: 7.817,
                type: WinRatioType.SUPER_DUPER_MEGA_WIN
            }
        ]
    };

    PERFORMANCE_LOGGER: boolean = true;

    BONUS_GAME_TRAIN_STOPPED: boolean = false;

    // #353
    WIN_BELOW_STAKE: boolean = false;

    private constructor() {
        if (FITHGameConfig.instance) {
            throw new Error("GameConfig.constructor() - Instantiation failed: Singleton.");
        }
    }

    REEL_SPIN_STOP_SYMBOL_STATE: () => ISymbolStateConfig = () => {
        return FITHGameConfig.instance.SYMBOL_STATES.land;
    };
}